import React from 'react';
import clsx from 'clsx';
import { FormItem, FormInputPhone } from '@/components/dashboard/settings';
import {
	AddressBlock,
	InputCheck,
	InputEmail,
	InputText,
} from '@/components/form';
import { Alert, Button, LoaderScreen, Typography } from '@/components/common';
import { UserI } from '@/interfaces';
import Icons from '@/const/icons';
import { PersonalInformationReturn } from './types';

interface FormPhaseBodyProps {
	form: PersonalInformationReturn['form'];
	rules: PersonalInformationReturn['rules'];
	userInformation: UserI | undefined;
	isVerified: PersonalInformationReturn['isVerified'];
	initialPhone: PersonalInformationReturn['initialPhone'];
	refetch: () => void;
	showLoader: boolean;
}

export const FormPhaseBody: React.FC<FormPhaseBodyProps> = ({
	form,
	rules,
	userInformation,
	isVerified,
	initialPhone,
	refetch,
	showLoader,
}) => {
	const {
		register,
		setValue,
		watch,
		formState: { errors },
	} = form;

	if (showLoader) {
		return (
			<div className="min-h-[280px] flex items-center justify-center">
				<LoaderScreen />
			</div>
		);
	}

	return (
		<>
			<Typography type="semibold-18" className="!text-left">
				Review and complete your personal details below. We&apos;ll use this
				information to verify your identity and confirm your eligibility as a
				claimant.
			</Typography>
			<div className="flex flex-col lg:flex-row gap-4 mt-6">
				<Alert
					type="warning"
					text="Important Legal Notice"
					className="px-4 !py-4 h-fit lg:max-w-[334px]"
					classNameText="!text-black !font-semibold !text-left !text-sm"
					classNameDescription="!text-left"
					description="The information provided in this section is used for official legal claims and documentation. You must ensure that all details entered are strictly accurate and truthful. Providing false, misleading, or fraudulent information may result in the immediate dismissal of your case."
				/>
				<div className="md:max-h-[290px] 2xl:max-h-[440px] overflow-visible md:overflow-y-auto md:scroll-custom md:pr-1 lg:bg-[#F9F9F9] lg:rounded lg:p-3">
					<div className="!space-y-4">
						<FormItem
							label="Name"
							className="!flex-col space-y-4 border-none !py-0"
						>
							<div className="flex flex-col md:flex-row items-baseline gap-4 w-full">
								<InputText
									name="firstName"
									register={register}
									placeholder="First name"
									className="w-full"
									classNameInput="!mt-0"
									rules={rules.firstName}
									error={errors.firstName}
								/>
								<InputText
									name="lastName"
									register={register}
									placeholder="Last name"
									className="w-full"
									classNameInput="!mt-0"
									rules={rules.lastName}
									error={errors.lastName}
								/>
							</div>
						</FormItem>
						<FormItem
							label="Email"
							className="!items-baseline !flex-col space-y-4 border-none !py-0"
						>
							{userInformation && (
								<InputEmail
									readOnly
									name="email"
									register={register}
									placeholder="lila@vans@gmail.com"
									className="w-full"
									classNameInput={clsx(
										'pr-10 md:pr-0 !mt-0 disabled:!bg-[#0D061614] disabled:text-black',
										{
											'!border-main-red !bg-bg-white': !isVerified,
											'!bg-transparent': isVerified,
										}
									)}
									setValueCustom={setValue}
									disabled
								/>
							)}
						</FormItem>
						<FormItem
							label="Phone"
							additionalInfoLabel="(optional)"
							className="!flex-col space-y-4 border-none !py-0"
						>
							<FormInputPhone
								refetch={refetch}
								register={register}
								setValue={setValue}
								phoneNumber={watch('phone')}
								initialPhoneValue={initialPhone}
								isPhoneVerified={false}
								error={errors.phone}
								// rules.phone marks the field required; leave it off so an
								// empty phone stays valid. InputPhone still applies minLength
								// 10, and `error` shows that message for a partial number.
								classNameInput="md:!w-full"
							/>
						</FormItem>
						{userInformation && (
							<AddressBlock
								register={register}
								setValue={setValue}
								watch={watch}
								errors={errors}
								rules={rules}
								defaultAddress1={userInformation.address1 || ''}
								initialCityName={userInformation.city?.name || ''}
								initialStateName={userInformation.state?.name || ''}
								initialZip={userInformation.zip_code || ''}
								className="!flex-col space-y-4 border-none !py-0"
								classNameInput="md:!w-full"
							/>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

interface FormPhaseFooterProps {
	register: PersonalInformationReturn['form']['register'];
	isConfirmed: boolean;
	onClose: () => void;
	onContinue: () => void;
	isSubmitting?: boolean;
}

export const FormPhaseFooter: React.FC<FormPhaseFooterProps> = ({
	register,
	isConfirmed,
	onClose,
	onContinue,
	isSubmitting = false,
}) => (
	<>
		<div className="flex justify-end">
			<InputCheck name="valid" register={register}>
				I confirm that the information provided is correct.
			</InputCheck>
		</div>
		<div className="flex items-center justify-between gap-3 mt-6">
			<Button
				decoration="secondary"
				onClick={onClose}
				disabled={isSubmitting}
				size="fit"
			>
				Close
			</Button>
			<Button
				decoration="primary"
				icon={Icons.arrowRight}
				iconRight
				disabled={!isConfirmed || isSubmitting}
				loading={isSubmitting}
				onClick={onContinue}
				size="fit"
				label="Continue"
			/>
		</div>
	</>
);
